import React, { useMemo, useState } from 'react';
import { cloneDeep } from 'lodash';
import { useFormik, yupToFormErrors, validateYupSchema } from 'formik';
import { CSSObjectWithLabel } from 'react-select';
import dayjs from 'dayjs';
import { useSetRecoilState } from 'recoil';

import AgModalWindow from '@/components/common/ModalWindow/AgModalWindow';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import ActionButtonsWrapper
  from '@/components/common/ModalWindow/ActionButtonsWrapper/ActionButtonsWrapper';
import useUpdateBulkDnr, { UpdateBulkDnrDto } from '../hooks/useUpdateBulkDnr';
import { isDnrDateOutOfRenewalRange } from '../components/utils';
import { FormattingUtils } from '@/utils/Formatting.utils';
import LinesCount from '@/components/common/ModalWindow/LinesCount/LinesCount';
import ModalFormDatepicker from '@/components/business/ModalForm/ModalFormDatepicker/ModalFormDatepicker';
import ModalFormDropdown from '@/components/business/ModalForm/ModalFormDropdown/ModalFormDropdown';
import ModalFormTextarea from '@/components/business/ModalForm/ModalFormTextarea/ModalFormTextarea';
import Toggle from '@/components/common/Toggle/Toggle';
import TopLabel from '@/components/common/TopLabel/TopLabel';
import ReadonlyField from '@/components/common/ModalWindow/ReadonlyField/ReadonlyField';
import { DEFAULT_DATE_PATTERN } from '@/config/common.config';
import { getDateWithProperTimeOffset, isValidDate } from '@/utils/date.utils';
import { DnrContractLinesFormFields, validationSchema } from '../dnrBulkUpdateModal/validationSchema';
import { DNR_TYPE } from '@/config/reasonTypes.config';
import DnrLinesTable from './DnrLinesTable';
import { rightsidePanel_isOpen } from '@/pages/ContractDetails/RightPanel/RightPanel.state';
import {
  clearLinesSelection
} from '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/linesSelectionObservable';

import styles from './dnr-bulk-update-modal.module.scss';

interface IDnrContractReasonModalProps {
  onClose: () => void;
  dnrContractLines: ContractLine[];
  contract: Contract;
  reasonCodes: ReasonCode[];
  reasonTypes: ReasonType[];
  isLoadingReasonCodes?: boolean;
  readOnly?: boolean;
}

export default function DnrBulkUpdateModal({
  dnrContractLines,
  onClose,
  contract,
  reasonCodes,
  reasonTypes,
  isLoadingReasonCodes,
  readOnly
}: IDnrContractReasonModalProps) {
  const [isConfirmWindowVisible, setIsConfirmWindowVisible] = useState<boolean>(false);
  const [isDnrWarningWindowVisible, setIsDnrWarningWindowVisible] = useState<boolean>(false);
  const [editedFormFields, setEditedFormFields] = useState({
    reasonCode: false,
    dnrDate: false,
    comment: false,
    noFurtherBilling: false,
    noFurtherBillingDate: false,
  });
  const { updateBulkDnr } = useUpdateBulkDnr();
  const setIsOpenRightPanel = useSetRecoilState(rightsidePanel_isOpen);

  const mutableContractLineSource = useMemo(() => {
    return cloneDeep(dnrContractLines);
  }, [dnrContractLines]);

  const isSingleLine = dnrContractLines.length === 1;
  const singleLine = isSingleLine ? dnrContractLines[0] : null;

  const initialValues = useMemo(() => {
    if (singleLine) {
      return {
        reasonCode: (singleLine.statusChangeReason?.code ?? singleLine.statusChangeReason ?? '') as string,
        comment: (singleLine.statusChangeComment ?? '') as string,
        dnrDate: singleLine.statusChangeDate
          ? getDateWithProperTimeOffset(new Date(singleLine.statusChangeDate))
          : getDateWithProperTimeOffset(
            contract?.contractEndDate ? new Date(contract.contractEndDate) : new Date()
          ),
        noFurtherBilling: singleLine.noFurtherBilling ?? false,
        noFurtherBillingDate: singleLine.noFurtherBillingDate
          ? getDateWithProperTimeOffset(new Date(singleLine.noFurtherBillingDate))
          : undefined,
      };
    }

    return {
      reasonCode: '',
      comment: '',
      dnrDate: getDateWithProperTimeOffset(
        contract?.contractEndDate ? new Date(contract?.contractEndDate) : new Date()
      ),
      noFurtherBilling: false,
      noFurtherBillingDate: undefined,
    };
  }, [contract, singleLine]);

  const validate = async (formValues: DnrContractLinesFormFields) => {
    const context = {
      contractEndDate: contract?.contractEndDate,
      contractRenewableTerm: contract?.renewableTerm
    };

    try {
      await validateYupSchema(formValues, validationSchema, true, context);
    }
    catch (err) {
      return yupToFormErrors(err);
    }

    return {};
  };

  const { values, setFieldValue, errors, setFieldTouched, touched } = useFormik<DnrContractLinesFormFields>({
    initialValues,
    validationSchema,
    validate,
    onSubmit: () => undefined,
    validateOnMount: true,
    enableReinitialize: true,
  });

  const onSubmitFormSave = (event: React.FormEvent) => {
    event.preventDefault();

    if (editedFormFields.dnrDate && values.dnrDate) {
      const dnrDateOutOfRenewalRange = isDnrDateOutOfRenewalRange(values.dnrDate, contract.contractEndDate);
      if (dnrDateOutOfRenewalRange) {
        setIsDnrWarningWindowVisible(true);
        return;
      }
    }

    setIsConfirmWindowVisible(true);
  };

  const onConfirm = () => {
    const updatedData: UpdateBulkDnrDto = {
      contractId: contract.contractID,
      dnrContractLineData: mutableContractLineSource.map((cl) => {
        const contractLineID = 'iD' in cl ? cl.iD : 'id' in cl ? cl.id : cl.contractLineID;

        // Get original values from contract line
        const originalReasonCode = (cl.statusChangeReason?.code ?? cl.statusChangeReason) as string;
        const originalDnrDate = cl.statusChangeDate;
        const originalComment = cl.statusChangeComment as string;
        const originalNoFurtherBilling = cl.noFurtherBilling ?? false;
        const originalNoFurtherBillingDate = cl.noFurtherBillingDate;

        // Use form values only if the field was edited, otherwise keep original
        const dnrDate = editedFormFields.dnrDate ? values.dnrDate : originalDnrDate;
        const reasonCode: string = editedFormFields.reasonCode
          ? (values.reasonCode ?? originalReasonCode)
          : originalReasonCode;
        const comment: string = editedFormFields.comment
          ? (values.comment ?? originalComment)
          : originalComment;

        let noFurtherBilling: boolean;
        let noFurtherBillingDate: Date | null | undefined;

        if (editedFormFields.noFurtherBilling || editedFormFields.noFurtherBillingDate) {
          noFurtherBilling = editedFormFields.noFurtherBilling
            ? (values.noFurtherBilling ?? false)
            : originalNoFurtherBilling;
          noFurtherBillingDate = editedFormFields.noFurtherBillingDate
            ? values.noFurtherBillingDate
            : originalNoFurtherBillingDate;
        } else {
          noFurtherBilling = originalNoFurtherBilling;
          noFurtherBillingDate = originalNoFurtherBillingDate;
        }

        return {
          contractLineID,
          dnrDate,
          reasonCode,
          comment,
          noFurtherBilling,
          noFurtherBillingDate,
        };
      }),
    };

    updateBulkDnr(updatedData);
    clearLinesSelection();
    setIsOpenRightPanel(false);
    onClose();
  };

  const onCloseDnrWarningWindow = () => {
    setIsDnrWarningWindowVisible(false);
  };

  const onCloseConfirmWindow = () => {
    setIsConfirmWindowVisible(false);
  };

  const hasAnyFormFieldBeenEdited = Object.values(editedFormFields).some(Boolean);
  const hasNoFurtherBillingDateError = editedFormFields.noFurtherBillingDate && !!errors?.noFurtherBillingDate;

  return (
    <>
      {isDnrWarningWindowVisible && (
        <AgConfirmationModal
          isOpen={true}
          title="You are about to set DNR date that is out of Contract Renewal window"
          onClose={onCloseDnrWarningWindow}
          onConfirm={onConfirm}
          contentStyle={{ width: 680 }}
        >
          The DNR Date set for the Contract/Contract Lines are out of range of Contract Renewal.
          <br />
          Please check the DNR Date and confirm the selection.
        </AgConfirmationModal>
      )}

      {isConfirmWindowVisible && (
        <AgConfirmationModal
          isOpen={true}
          title="You are about to update multiple DNR Contract Lines"
          onClose={onCloseConfirmWindow}
          onConfirm={onConfirm}
          contentStyle={{ width: 600 }}
        >
          Updating information for DNR Contract Lines could impact billing.<br />
          Use it only with extra precaution.
        </AgConfirmationModal>
      )}

      <AgModalWindow
        title="Edit DNR Details"
        isOpen={true}
        onClose={onClose}
        contentStyle={{ width: '90vw' }}
      >
        {readOnly && (
          <div
            style={{
              padding: '10px 15px',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              fontSize: '13px',
              color: '#856404',
              marginBottom: '15px',
            }}
          >
            <strong>Note: </strong>Only users with Administrator or Full Access roles can edit information.
          </div>
        )}

        <LinesCount className="mb-10">
          {`${dnrContractLines.length} ${FormattingUtils.pluralize(dnrContractLines.length, 'Line')} Selected`}
        </LinesCount>

        <div className={styles['section-label']}>Update details for all lines</div>

        <form onSubmit={onSubmitFormSave}>
          <div className={styles['dnr-bulk-update-container']}>
            <div className={styles['dnr-bulk-update-main']}>
              <div className={styles['dnr-bulk-update-main-row']}>
                <ModalFormDropdown<ReasonType>
                  id="dnrReasonType"
                  labelTitle="Reason Type"
                  className={styles['row-field']}
                  optionsData={reasonTypes}
                  getOptionLabel={(option) => option.formattedType}
                  getOptionValue={(option) => option.type}
                  disabled
                  defaultValue={reasonTypes.find((el) => el.type === DNR_TYPE)}
                />

                <ModalFormDropdown<ReasonCode>
                  id="dnrReason"
                  labelTitle="Reason Name"
                  isSearchable={true}
                  className={styles['row-field']}
                  optionsData={reasonCodes}
                  placeholder="Select or type to search"
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.code}
                  selectedOption={reasonCodes.find((el) => el.code === values.reasonCode) ?? null}
                  customStyles={{
                    menuList: (provided) => ({ ...provided, maxHeight: 200 } as CSSObjectWithLabel)
                  }}
                  menuPortalTarget={document.querySelector('body')}
                  handleChange={(newValue) => {
                    setFieldTouched('reasonCode', true, false);
                    setFieldValue('reasonCode', newValue?.code);
                    setEditedFormFields(prev => ({ ...prev, reasonCode: true }));
                  }}
                  validationMessage={
                    (errors?.reasonCode && touched.reasonCode) ? errors.reasonCode : undefined
                  }
                  isLoading={isLoadingReasonCodes}
                  disabled={isLoadingReasonCodes}
                />
              </div>

              <div className={`${styles['dnr-bulk-update-main-row']} mt-2`}>
                <ModalFormDatepicker
                  id="dnrDate"
                  labelTitle="DNR Date"
                  className={styles['row-field']}
                  onChange={({ value }) => {
                    setFieldTouched('dnrDate', true, false);
                    setFieldValue('dnrDate', value);
                    setEditedFormFields(prev => ({ ...prev, dnrDate: true }));
                  }}
                  value={
                    values.dnrDate && isValidDate(values.dnrDate as Date)
                      ? new Date(values.dnrDate as Date)
                      : undefined
                  }
                  validationMessage={
                    (errors?.dnrDate && touched.dnrDate) ? errors.dnrDate as string : undefined
                  }
                />

                <div className={styles['row-field']}>
                  <TopLabel label="Contract End Date" />
                  <ReadonlyField>
                    {contract?.contractEndDate
                      ? dayjs(contract.contractEndDate).format(DEFAULT_DATE_PATTERN)
                      : 'Not specified'}
                  </ReadonlyField>
                </div>

                <div className={styles['row-field']}>
                  <TopLabel label="Contract Term (Months)" />
                  <ReadonlyField>{contract?.term}</ReadonlyField>
                </div>
              </div>

              <div className={`${styles['no-further-billing-section']} mt-2`}>
                <div className="mr-10">
                  <Toggle
                    id="no-further-billing"
                    isEnable={values.noFurtherBilling}
                    onChange={() => {
                      const newValue = !values.noFurtherBilling;
                      setFieldTouched('noFurtherBilling', true, false);
                      setFieldValue('noFurtherBilling', newValue);
                      setEditedFormFields(prev => ({ ...prev, noFurtherBilling: true }));

                      if (newValue === false) {
                        setFieldValue('noFurtherBillingDate', undefined);
                      }
                      else {
                        setFieldValue('noFurtherBillingDate', getDateWithProperTimeOffset(new Date()));
                        setEditedFormFields(prev => ({ ...prev, noFurtherBillingDate: true }));
                      }
                    }}
                  />
                </div>

                <TopLabel id="no-further-billing" label="No Further Billing" withMargin={false} />
              </div>

              <div className={`${styles['dnr-bulk-update-main-row']} mt-2 
              ${values.noFurtherBilling ? '' : 'invisible'}`}>
                <ModalFormDatepicker
                  id="noFurtherBillingDate"
                  labelTitle="No Further Billing Date"
                  className={`${styles['row-field']} ${styles['row-field-third']}`}
                  onChange={({ value }) => {
                    setFieldTouched('noFurtherBillingDate', true, false);
                    setFieldValue('noFurtherBillingDate', value);
                    setEditedFormFields(prev => ({ ...prev, noFurtherBillingDate: true }));
                  }}
                  value={values.noFurtherBillingDate}
                  validationMessage={
                    (errors?.noFurtherBillingDate && touched.noFurtherBillingDate)
                      ? errors.noFurtherBillingDate
                      : undefined
                  }
                />
              </div>
            </div>

            <div className={styles['dnr-bulk-update-comment']}>
              <ModalFormTextarea
                id="dnrComment"
                labelTitle="Comment"
                className={styles['dnr-bulk-update-comment-field']}
                placeholder="Enter short comments"
                value={values.comment ?? ''}
                onChange={(event) => {
                  setFieldTouched('comment', true, false);
                  setFieldValue('comment', event.target.value);
                  setEditedFormFields(prev => ({ ...prev, comment: true }));
                }}
                validationMessage={
                  (errors?.comment && touched.comment) ? errors.comment : undefined
                }
                isFloatingValidation={true}
              />
            </div>
          </div>

          <div className={styles.button}>
            <Button
              className={styles['button-save']}
              disabled={!hasAnyFormFieldBeenEdited || hasNoFurtherBillingDateError || readOnly}
              buttonStyleType={ButtonStyleType.PRIMARY}
              type="submit"
            >
              Save
            </Button>
          </div>
        </form>

        <DnrLinesTable
          dnrContractLines={dnrContractLines}
          contract={contract}
        />

        <ActionButtonsWrapper
          leftButton={(
            <Button
              buttonStyleType={ButtonStyleType.SECONDARY}
              onClick={onClose}
            >
              Cancel
            </Button>
          )}
        />
      </AgModalWindow>
    </>
  );
}
